import { type QueryOptions } from 'apollo-client';
import type ApolloClient from 'apollo-client';
import { type ContextId, type ForgeUIExtensionType } from '@atlassian/forge-ui-types';

import type {
	GQLExtensionContextsFilter,
	GQLExtensionRenderingContextInput,
	UsePreFilteredExtensionsQueryOptions,
} from '../web-client';
import {
	createExtensionListQueryOptions,
	createUsePrefilteredExtensionListQueryOptions,
	getExtensionsFromGQLResult,
	getPrefilteredExtensionsGQLResult,
	type QueryData,
} from '../web-client';
import type { GraphQLError } from 'graphql';
import { fg } from '@atlaskit/platform-feature-flags';
import type {
	PreFilteredExtensionsQueryHookOptions,
	PrefilteredExtensionsResult,
} from '../web-client/hooks/useExtensionList';
import { FetchPreFilteredForgeAppsQuery } from '../web-client/hooks/lib/queries';

export type GetForgeUIExtensionsAsyncOptions = {
	bypassNewForgeExtensionListCall?: boolean;
	client: ApolloClient<object>;
	cloudId?: string;
	context?: GQLExtensionRenderingContextInput;
	contextIds: ContextId[];
	dataClassificationTagIds?: string[];
	expandAppOwner?: boolean;
	extensionsFilter?: GQLExtensionContextsFilter[];
	includeHidden?: boolean;
	locale?: string;
	moduleType?: string;
	queryOptions?: Omit<QueryOptions, 'query'>;
};

export const getForgeUIExtensionsAsync = async ({
	client,
	contextIds,
	moduleType,
	context,
	queryOptions,
	expandAppOwner,
	extensionsFilter,
	dataClassificationTagIds,
	locale,
	cloudId,
	includeHidden,
	bypassNewForgeExtensionListCall = false,
}: GetForgeUIExtensionsAsyncOptions): Promise<{
	errors?: ReadonlyArray<GraphQLError>;
	extensions: ReadonlyArray<ForgeUIExtensionType> | null;
}> => {
	if (bypassNewForgeExtensionListCall === false && fg('use_new_forge_extension_list_call')) {
		const types: string[] = moduleType ? [moduleType] : [];
		const requestOptions =
			createUsePrefilteredExtensionListQueryOptions<PreFilteredExtensionsQueryHookOptions>(
				cloudId ?? contextIds[0],
				context ?? {},
				client,
				types,
				locale,
				includeHidden,
				queryOptions as UsePreFilteredExtensionsQueryOptions,
				contextIds,
			);

		const { fetchPolicy, ...rest } = requestOptions;
		const { loading, data, errors } = await client.query<PrefilteredExtensionsResult>({
			...rest,
			query: FetchPreFilteredForgeAppsQuery,
			fetchPolicy: fetchPolicy === 'cache-and-network' ? 'network-only' : fetchPolicy,
		});
		const extensions = getPrefilteredExtensionsGQLResult<ForgeUIExtensionType>({ loading, data });
		return {
			extensions,
			errors: errors,
		};
	} else {
		const requestOptions = createExtensionListQueryOptions<QueryOptions>(
			client,
			contextIds,
			queryOptions as QueryOptions,
			expandAppOwner,
			extensionsFilter,
			moduleType,
			dataClassificationTagIds,
			locale,
		);

		const queryResult = await client.query<QueryData>(requestOptions);
		return {
			extensions: getExtensionsFromGQLResult<ForgeUIExtensionType>(queryResult),
			errors: queryResult.errors,
		};
	}
};
